import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe2, Boxes, Building2 } from 'lucide-react';
import { useRef, useState, useEffect } from 'react';
import heroProducts from './heroProducts.json';
import { HardwareCategoryStrip } from './HardwareCategoryStrip';

const HERO_BG = '/hero/hero-bg.webp';
const ROTATION_MS = 4200;
const { pool, rotation } = heroProducts;

const TRUST_SIGNALS = [
  { icon: Globe2, label: 'International Supply Network' },
  { icon: Boxes, label: 'B2B Technology Supply' },
  { icon: Building2, label: 'UK Registered Company' },
];

// Fixed-size card shell; only the inner content crossfades so the grid never shifts
const HardwareCard = ({ item, slot, setIndex }) => {
  const eager = setIndex === 0;
  return (
    <Link
      to={item.to}
      className="relative block bg-white rounded-2xl border border-slate-200/70 overflow-hidden group hover:border-[#0096D6]/40 transition-colors duration-300"
      style={{ boxShadow: '0 4px 24px rgba(15,23,42,0.06)' }}
      aria-label={`View ${item.name} collection`}
      data-testid={`hero-card-${slot}`}
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#0096D6]/50 to-transparent" />
      <div className="p-3 sm:p-5 flex flex-col">
        <motion.span
          key={`badge-${item.name}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="self-start whitespace-nowrap px-2.5 py-1 rounded-full text-[9px] sm:text-[10px] font-semibold tracking-[0.08em] sm:tracking-[0.12em] font-manrope bg-[#0096D6]/8 text-[#0096D6] border border-[#0096D6]/15"
          data-testid={`hero-card-badge-${slot}`}
        >
          {item.badge}
        </motion.span>

        <div className="relative mt-3 w-full aspect-[4/3] rounded-xl bg-white overflow-hidden transition-transform duration-500 group-hover:scale-[1.04]">
          <AnimatePresence initial={false}>
            <motion.img
              key={item.src}
              src={item.src}
              alt={item.name}
              width={1200}
              height={900}
              loading={eager ? 'eager' : 'lazy'}
              fetchPriority={eager ? 'high' : 'auto'}
              decoding="async"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.55, ease: 'easeOut' }}
              className="absolute inset-0 w-full h-full object-contain"
              data-testid={`hero-card-image-${slot}`}
            />
          </AnimatePresence>
        </div>

        <motion.span
          key={`name-${item.name}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="font-manrope text-xs sm:text-sm font-semibold text-slate-700 mt-3 text-center min-h-[2.5rem] flex items-center justify-center"
          data-testid={`hero-card-name-${slot}`}
        >
          {item.name}
        </motion.span>
      </div>
    </Link>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const [setIndex, setSetIndex] = useState(0);

  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);

  useEffect(() => {
    const t = setInterval(() => setSetIndex(i => (i + 1) % rotation.length), ROTATION_MS);
    return () => clearInterval(t);
  }, []);

  // Warm the browser cache for the remaining sets after first paint so rotation never flickers
  useEffect(() => {
    const t = setTimeout(() => {
      Object.values(pool).forEach(p => { const img = new Image(); img.src = p.src; });
    }, 1500);
    return () => clearTimeout(t);
  }, []);

  const scrollToCategories = () => {
    const el = document.getElementById('hardware-categories');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const current = rotation[setIndex].map(key => pool[key]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.2 } }
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
  };

  return (
    <section ref={ref} className="relative flex flex-col overflow-hidden lg:min-h-screen bg-[#F8FAFC]" data-testid="hero-section">
      {/* Background — subtle technical composition */}
      <motion.div className="absolute inset-0 z-0" style={{ y: bgY }}>
        <img src={HERO_BG} alt="" aria-hidden="true" className="w-full h-full object-cover object-right" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/50" />
      </motion.div>
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(15,23,42,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.045) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at 72% 45%, black 20%, transparent 70%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 72% 45%, black 20%, transparent 70%)',
        }}
      />
      <div className="absolute top-24 right-[10%] w-80 h-80 bg-[#0096D6]/10 rounded-full blur-3xl z-0 animate-float" />
      <div className="absolute bottom-1/3 left-10 w-96 h-96 bg-[#0096D6]/5 rounded-full blur-3xl z-0" />

      {/* Main Content */}
      <div className="relative z-20 flex-1 flex items-center w-full pt-28 sm:pt-32 pb-10 lg:pb-14">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">

            {/* Left — Positioning */}
            <motion.div variants={containerVariants} initial="hidden" animate="visible" className="lg:col-span-6">
              <motion.div variants={itemVariants} className="mb-6">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0096D6]/10 text-[#0096D6] font-manrope text-xs sm:text-sm font-semibold tracking-[0.14em] uppercase" data-testid="hero-top-label">
                  <span className="w-2 h-2 rounded-full bg-[#0096D6] animate-pulse" />
                  Global Technology Hardware Wholesale
                </span>
              </motion.div>

              <motion.h1
                variants={itemVariants}
                className="font-outfit font-bold text-slate-900 uppercase tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-[2.9rem] xl:text-[3.25rem] mb-6"
                style={{ textWrap: 'balance' }}
                data-testid="hero-headline"
              >
                <span className="block">High-Performance Hardware.</span>
                <span className="block gradient-text-cyan">Global Wholesale Supply.</span>
              </motion.h1>

              <motion.p variants={itemVariants} className="font-manrope text-base md:text-lg text-slate-600 leading-relaxed mb-3 max-w-xl" data-testid="hero-subtext">
                Source GPUs, AI infrastructure, processors, memory, enterprise storage and high-demand technology hardware through our international wholesale network.
              </motion.p>

              <motion.p variants={itemVariants} className="font-manrope text-sm md:text-base text-slate-500 mb-8 max-w-xl" data-testid="hero-audience">
                Built for resellers, system integrators, infrastructure providers and technology businesses.
              </motion.p>

              <motion.div variants={itemVariants} className="flex flex-wrap gap-4 mb-8">
                <Link to="/contact#enquiry-form" className="btn-primary inline-flex items-center gap-2 text-base" data-testid="hero-cta-contact">
                  Receive Wholesale Offers <ArrowRight className="w-5 h-5" />
                </Link>
                <button type="button" onClick={scrollToCategories} className="btn-secondary inline-flex items-center gap-2 text-base" data-testid="hero-cta-categories">
                  Explore Hardware Categories
                </button>
              </motion.div>

              <motion.div variants={itemVariants} className="flex flex-wrap gap-x-6 gap-y-3" data-testid="hero-trust-signals">
                {TRUST_SIGNALS.map((signal, i) => (
                  <div key={signal.label} className="flex items-center gap-2" data-testid={`hero-trust-signal-${i}`}>
                    <span className="w-7 h-7 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center">
                      <signal.icon className="w-3.5 h-3.5 text-[#0096D6]" />
                    </span>
                    <span className="font-manrope text-sm text-slate-600 font-medium">{signal.label}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right — Hardware Matrix */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.7, ease: 'easeOut' }}
              className="lg:col-span-6 relative"
              data-testid="hero-product-showcase"
            >
              <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-[#0096D6]/10 via-transparent to-slate-200/50 blur-2xl -z-10" />
              <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-5 max-w-xl mx-auto lg:max-w-none">
                {current.map((item, slot) => (
                  <HardwareCard key={slot} item={item} slot={slot} setIndex={setIndex} />
                ))}
              </div>
            </motion.div>

          </div>
        </div>
      </div>

      <HardwareCategoryStrip />
    </section>
  );
};
